import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Trophy, X, Upload } from 'lucide-react';
import { achievementsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ImageUpload } from '../upload/ImageUpload';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

// Schwierigkeitsstufen mit RGB-Farben
const DIFFICULTY_OPTIONS = [
    { name: 'Very Easy', color: 'rgb(61, 92, 61)' },
    { name: 'Easy', color: 'rgb(45, 136, 46)' },
    { name: 'Medium', color: 'rgb(139, 93, 0)' },
    { name: 'Hard', color: 'rgb(177, 59, 59)' },
    { name: 'Very Hard', color: 'rgb(88, 0, 133)' },
    { name: 'Extrem', color: 'rgb(163, 181, 0)' },
];

const getDifficultyColor = (difficulty) => {
    const option = DIFFICULTY_OPTIONS.find(d => d.name === difficulty);
    return option?.color || 'rgb(139, 93, 0)';
};

export const AchievementManager = () => {
    const { user } = useAuth();
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAchievement, setEditingAchievement] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [achievementToDelete, setAchievementToDelete] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        image_url: '',
        values_by_rarity: []
    });


    useEffect(() => {
        loadAchievements();
    }, []);

    const loadAchievements = async () => {
        setLoading(true);
        try {
            const response = await achievementsApi.getAll();
            const data = response.data;
            setAchievements(Array.isArray(data) ? data : (data.items || []));
        } catch (error) {
            console.error('Failed to load achievements:', error);
            toast.error('Fehler beim Laden der Achievements');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingAchievement(null);
        setFormData({
            title: '',
            image_url: '',
            values_by_rarity: []
        });
        setModalOpen(true);
    };

    const openEditModal = (achievement) => {
        setEditingAchievement(achievement);
        setFormData({
            title: achievement.title || '',
            image_url: achievement.image_url || '',
            values_by_rarity: achievement.values_by_rarity || []
        });
        setModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingAchievement) {
                await achievementsApi.update(editingAchievement.id, formData, user?.discord_id, user?.username);
            } else {
                await achievementsApi.create(formData, user?.discord_id, user?.username);
            }
            setModalOpen(false);
            loadAchievements();
        } catch (error) {
            console.error('Failed to save achievement:', error);
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDelete = async () => {
        if (!achievementToDelete) return;
        try {
            await achievementsApi.delete(achievementToDelete.id, user?.discord_id, user?.username);
            toast.success('Achievement gelöscht');
            setDeleteConfirmOpen(false);
            setAchievementToDelete(null);
            loadAchievements();
        } catch (error) {
            console.error('Failed to delete achievement:', error);
            toast.error('Fehler beim Löschen');
        }
    };

    const addNewTier = () => {
        const newTierName = `Stufe ${formData.values_by_rarity.length + 1}`;
        setFormData(prev => ({
            ...prev,
            values_by_rarity: [...prev.values_by_rarity, { 
                rarity: newTierName, 
                description: '', 
                rewards: '', 
                is_maxed: false,
                difficulty: 'Medium',
                image_url: ''
            }]
        }));
    };

    const removeTier = (index) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.filter((_, idx) => idx !== index)
        }));
    };

    const updateTierData = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === index ? { ...item, [field]: value } : item
            )
        }));
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                        <Trophy className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-white">Achievements</h2>
                        <p className="text-sm text-gray-500">{achievements.length} Einträge</p>
                    </div>
                </div>
                <Button onClick={openCreateModal} className="bg-cyan-500 hover:bg-cyan-400 text-black">
                    <Plus size={18} className="mr-2" />
                    Neues Achievement
                </Button>
            </div>

            {/* Achievement List */}
            <div className="space-y-3">
                {loading ? (
                    <div className="text-center py-8 text-gray-500">Laden...</div>
                ) : achievements.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">Keine Achievements vorhanden</div>
                ) : (
                    achievements.map((achievement) => (
                        <div
                            key={achievement.id}
                            className="flex items-center gap-4 p-4 rounded-xl bg-card/50 border border-white/5 hover:border-white/10 transition-colors group"
                        >
                            {achievement.image_url && (
                                <img
                                    src={achievement.image_url}
                                    alt={achievement.title}
                                    className="w-16 h-16 object-contain rounded-lg bg-black/20"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-white truncate">{achievement.title}</h3>
                                <p className="text-sm text-gray-500">
                                    {achievement.values_by_rarity?.length || 0} Seltenheiten konfiguriert
                                </p>
                            </div>
                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditModal(achievement)}
                                    className="text-gray-400 hover:text-cyan-400"
                                >
                                    <Pencil size={16} />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                        setAchievementToDelete(achievement);
                                        setDeleteConfirmOpen(true);
                                    }}
                                    className="text-gray-400 hover:text-red-400"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            {editingAchievement ? 'Achievement bearbeiten' : 'Neues Achievement'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Info */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Titel *</Label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Achievement Titel"
                                    className="bg-black/40 border-white/10"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Bild</Label>
                                <ImageUpload
                                    currentImage={formData.image_url}
                                    onUpload={(url) => setFormData(prev => ({ ...prev, image_url: url || '' }))}
                                />
                                <div className="text-xs text-gray-500">Oder URL direkt eingeben:</div>
                                <Input
                                    value={formData.image_url}
                                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                    placeholder="https://..."
                                    className="bg-black/40 border-white/10"
                                />
                            </div>
                        </div>

                        {/* Tiers/Stufen Configuration */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-gray-400 text-lg">Stufen konfigurieren</Label>
                                <Button
                                    type="button"
                                    onClick={addNewTier}
                                    variant="outline"
                                    size="sm"
                                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                                >
                                    <Plus size={16} className="mr-1" />
                                    Stufe hinzufügen
                                </Button>
                            </div>
                            
                            {formData.values_by_rarity.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 border border-dashed border-white/10 rounded-xl">
                                    Keine Stufen angelegt. Klicke auf "Stufe hinzufügen" um zu beginnen.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {formData.values_by_rarity.map((tierData, index) => (
                                        <div
                                            key={index}
                                            className="p-4 rounded-xl border"
                                            style={{ 
                                                borderColor: `${getDifficultyColor(tierData.difficulty)}80`,
                                                backgroundColor: `${getDifficultyColor(tierData.difficulty)}15`
                                            }}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex-1 mr-4">
                                                    <Input
                                                        value={tierData.rarity}
                                                        onChange={(e) => updateTierData(index, 'rarity', e.target.value)}
                                                        placeholder="Stufenname (z.B. Bronze, Silber, Gold)"
                                                        className="bg-black/40 border-white/10 h-9 font-semibold"
                                                        style={{ borderColor: getDifficultyColor(tierData.difficulty) }}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center gap-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                                                        <input
                                                            type="checkbox"
                                                            id={`maxed-${index}`}
                                                            checked={tierData.is_maxed || false}
                                                            onChange={(e) => updateTierData(index, 'is_maxed', e.target.checked)}
                                                            className="w-4 h-4 rounded border-yellow-500/50 bg-black/40 text-yellow-500"
                                                        />
                                                        <label htmlFor={`maxed-${index}`} className="text-sm text-yellow-400 cursor-pointer">
                                                            Maxed
                                                        </label>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeTier(index)}
                                                        className="text-gray-400 hover:text-red-400 h-8 w-8"
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Schwierigkeit Dropdown */}
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-gray-500">Schwierigkeit</Label>
                                                    <Select 
                                                        value={tierData.difficulty || 'Medium'} 
                                                        onValueChange={(value) => updateTierData(index, 'difficulty', value)}
                                                    >
                                                        <SelectTrigger className="bg-black/40 border-white/10 h-9">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                                                            {DIFFICULTY_OPTIONS.map((option) => (
                                                                <SelectItem key={option.name} value={option.name}>
                                                                    <span className="flex items-center gap-2">
                                                                        <span 
                                                                            className="w-3 h-3 rounded-full" 
                                                                            style={{ backgroundColor: option.color }}
                                                                        />
                                                                        {option.name}
                                                                    </span>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-gray-500">Bild URL</Label>
                                                    <Input
                                                        value={tierData.image_url || ''}
                                                        onChange={(e) => updateTierData(index, 'image_url', e.target.value)}
                                                        placeholder="https://..."
                                                        className="bg-black/40 border-white/10 h-9 text-sm"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-gray-500">Beschreibung</Label>
                                                    <Input
                                                        value={tierData.description || ''}
                                                        onChange={(e) => updateTierData(index, 'description', e.target.value)}
                                                        placeholder="Beschreibung für diese Stufe"
                                                        className="bg-black/40 border-white/10 h-9 text-sm"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-gray-500">Belohnungen</Label>
                                                    <Input
                                                        value={tierData.rewards || ''}
                                                        onChange={(e) => updateTierData(index, 'rewards', e.target.value)}
                                                        placeholder="z.B. 500 Gold, 10 Gems"
                                                        className="bg-black/40 border-white/10 h-9 text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                            <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-black">
                                {editingAchievement ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Achievement löschen?</DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-400">
                        Möchtest du "{achievementToDelete?.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
                    </p>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-400 text-white">
                            Löschen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
